import type { ComponentType } from "react";
import * as Flags from "country-flag-icons/react/3x2";
import { hasFlag } from "country-flag-icons";
import { COUNTRY_OPTIONS } from "../../lib/countries";
import styles from "./TeamFlag.module.css";

export interface TeamFlagProps {
  /** ISO 3166-1 alpha-2, as `team_country` carries it. */
  code: string;
  /** Print the code beside the flag. Off inside a tight cell. */
  withCode?: boolean;
}

/**
 * The state a club represents, on a start list or an entries table.
 *
 * Rendered only where the server said so: `team_country` is filled for a
 * club outside ČNSMS and empty for one of ours, so a Czech start list is not
 * a column of identical flags.
 *
 * SVG rather than the regional-indicator emoji: Windows browsers render
 * those as bare letters („SK"), which on a printed startovní listina is
 * worse than no flag at all. `country-flag-icons` is data, not an icon set,
 * so it does not contradict the lucide-only rule (AGENTS §3).
 */
export function TeamFlag({ code, withCode = true }: TeamFlagProps) {
  const cc = code.trim().toUpperCase();
  if (!cc) return null;
  const country = COUNTRY_OPTIONS.find((c) => c.value === cc)?.label ?? cc;
  // An unknown or not-yet-drawn code still says which state it is: the
  // code itself is the fallback, never an empty box.
  const byCode = Flags as unknown as Record<
    string,
    ComponentType<{ title?: string; className?: string }> | undefined
  >;
  const Flag = hasFlag(cc) ? byCode[cc] : undefined;
  return (
    <span className={styles.wrap}>
      {Flag ? <Flag title={country} className={styles.flag} /> : null}
      {withCode && <span className={styles.code}>{cc}</span>}
      {!Flag && !withCode && <span className={styles.code}>{cc}</span>}
    </span>
  );
}
